import React from 'react';
import styled from 'styled-components';
import { Icon } from './Icon';

const FooterSection = styled.footer`
  background-color: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.white};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      radial-gradient(circle at 25% 25%, rgba(230, 126, 34, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(255, 255, 255, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const MainFooter = styled.div`
  padding: 4rem 0 2rem;
  position: relative;
  z-index: 1;
`;

const Container = styled.div`
  max-width: 75rem;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    padding: 0 1rem;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  gap: 3rem;
  margin-bottom: 3rem;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: 2fr 1fr 1fr;
    gap: 2.5rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }
`;

const FooterColumn = styled.div`
  &.company-info {
    @media (max-width: ${props => props.theme.breakpoints.desktop}) {
      grid-column: 1 / -1;
    }

    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      grid-column: 1 / -1;
    }
  }
`;

const CompanyLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    justify-content: center;
  }
`;

const LogoText = styled.div`
  font-family: ${props => props.theme.fonts.heading};
  font-weight: 700;
  font-size: 1.75rem;
  color: ${props => props.theme.colors.white};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.5rem;
  }
`;

const LogoSubtext = styled.div`
  font-size: 0.9rem;
  color: ${props => props.theme.colors.accent};
  font-weight: 600;
  margin-top: -0.25rem;
`;

const CompanyDescription = styled.p`
  color: ${props => props.theme.colors.lightGray};
  line-height: 1.6;
  margin-bottom: 2rem;
  max-width: 25rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    max-width: none;
  }
`;

const SocialMedia = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.8125rem;
  height: 2.8125rem;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  color: ${props => props.theme.colors.white};
  font-size: 1.25rem;
  transition: all 0.3s ease;
  text-decoration: none;

  &:hover {
    background-color: ${props => props.theme.colors.accent};
    transform: translateY(-0.1875rem);
    box-shadow: 0 0.5rem 1.25rem rgba(230, 126, 34, 0.3);
  }
`;

const ColumnTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.accent};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -0.5rem;
    left: 0;
    width: 1.875rem;
    height: 0.125rem;
    background-color: ${props => props.theme.colors.accent};
    border-radius: 0.0625rem;

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      left: 50%;
      transform: translateX(-50%);
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.1rem;
  }
`;

const FooterList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const FooterListItem = styled.li`
  margin-bottom: 0.75rem;
`;

const FooterLink = styled.a`
  color: ${props => props.theme.colors.lightGray};
  text-decoration: none;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: ${props => props.theme.colors.accent};
    transform: translateX(0.3125rem);
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    justify-content: center;

    &:hover {
      transform: none;
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 1rem;
  color: ${props => props.theme.colors.lightGray};
  font-size: 0.95rem;
  line-height: 1.5;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    justify-content: center;
    text-align: left;
  }
`;

const ContactIcon = styled.span`
  color: ${props => props.theme.colors.accent};
  font-size: 1.1rem;
  margin-top: 0.1rem;
  min-width: 1.25rem;
`;

const WhatsAppCTA = styled.div`
  background: linear-gradient(
    135deg,
    rgba(230, 126, 34, 0.2) 0%,
    rgba(230, 126, 34, 0.1) 100%
  );
  border: 0.125rem solid ${props => props.theme.colors.accent};
  border-radius: 0.9375rem;
  padding: 1.5rem;
  text-align: center;
  margin-top: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-column: 1 / -1;
  }
`;

const WhatsAppTitle = styled.h5`
  color: ${props => props.theme.colors.accent};
  font-size: 1.1rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const WhatsAppText = styled.p`
  color: ${props => props.theme.colors.lightGray};
  font-size: 0.9rem;
  margin-bottom: 1rem;
`;

const WhatsAppButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${props => props.theme.colors.accent};
  color: ${props => props.theme.colors.white};
  padding: 0.75rem 1.5rem;
  border-radius: 1.5625rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 0.9rem;

  &:hover {
    background-color: var(--action-accent-hover);
    transform: translateY(-0.125rem);
    box-shadow: 0 0.5rem 1.25rem rgba(230, 126, 34, 0.3);
  }
`;

const FooterBottom = styled.div`
  border-top: 0.0625rem solid rgba(255, 255, 255, 0.2);
  padding: 2rem 0;
  position: relative;
  z-index: 1;
`;

const BottomContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    flex-direction: column;
    text-align: center;
    gap: 1rem;
  }
`;

const Copyright = styled.p`
  color: ${props => props.theme.colors.lightGray};
  font-size: 0.9rem;
  margin: 0;
`;

const PoweredBy = styled.p`
  color: ${props => props.theme.colors.lightGray};
  font-size: 0.85rem;
  margin: 0;
  opacity: 0.8;

  a {
    color: ${props => props.theme.colors.accent};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 2rem;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const LegalLink = styled.a`
  color: ${props => props.theme.colors.lightGray};
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.3s ease;

  &:hover {
    color: ${props => props.theme.colors.accent};
  }
`;

export const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/529513214264?text=Hola, me interesa obtener más información sobre sus servicios.', '_blank');
  };

  const handleEmailClick = () => {
    window.open('mailto:contacto@vidrerialacanadita.com?subject=Consulta sobre servicios', '_self');
  };


  return (
    <FooterSection>
      <MainFooter>
        <Container>
          <FooterGrid>
            <FooterColumn className="company-info">
              <CompanyLogo>
                <LogoText>
                  Vidrería la Cañadita
                  <LogoSubtext>Vidrios y Aluminios</LogoSubtext>
                </LogoText>
              </CompanyLogo>
              
              <CompanyDescription>
                Especialistas en Puertas, Ventanas, Cancelería de baños, Barandales, Espejos, Vidrios de diferentes espesor. Trabajos garantizados en Col. La cañadita Santa María Atzompa.
              </CompanyDescription>

              <SocialMedia>
                <SocialLink 
                  href="https://facebook.com/vidrerialacanadita" 
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Facebook"
                >
                  <Icon name="Facebook" size={20} />
                </SocialLink>
                <SocialLink 
                  href="https://instagram.com/vidrerialacanadita" 
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Instagram"
                >
                  <Icon name="Instagram" size={20} />
                </SocialLink>
                <SocialLink 
                  onClick={handleWhatsAppClick}
                  title="WhatsApp"
                  style={{ cursor: 'pointer' }}
                >
                  <Icon name="MessageCircle" size={20} />
                </SocialLink>
                <SocialLink 
                  onClick={handleEmailClick}
                  title="Email"
                  style={{ cursor: 'pointer' }}
                >
                  <Icon name="Mail" size={20} />
                </SocialLink>
              </SocialMedia>

              <WhatsAppCTA>
                <WhatsAppTitle>¡Presupuesto Gratis por WhatsApp!</WhatsAppTitle>
                <WhatsAppText>
                  Obtén tu cotización en minutos. Nuestro equipo está listo para ayudarte.
                </WhatsAppText>
                <WhatsAppButton onClick={handleWhatsAppClick}>
                  <Icon name="MessageCircle" size={18} /> Chatear Ahora
                </WhatsAppButton>
              </WhatsAppCTA>
            </FooterColumn>

            <FooterColumn>
              <ColumnTitle>Servicios</ColumnTitle>
              <FooterList>
                <FooterListItem>
                  <FooterLink href="#servicios"><Icon name="DoorOpen" size={16} /> Puertas</FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#servicios"><Icon name="PanelsTopLeft" size={16} /> Ventanas</FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#servicios"><Icon name="Bath" size={16} /> Cancelería de baños</FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#servicios"><Icon name="PanelsTopLeft" size={16} /> Barandales</FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#servicios"><Icon name="Square" size={16} /> Espejos</FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#servicios"><Icon name="Grid2X2" size={16} /> Vidrios de diferentes espesor</FooterLink>
                </FooterListItem>
              </FooterList>
            </FooterColumn>

            <FooterColumn>
              <ColumnTitle>Enlaces</ColumnTitle>
              <FooterList>
                <FooterListItem>
                  <FooterLink href="#inicio"><Icon name="House" size={16} /> Inicio</FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#servicios"><Icon name="Settings" size={16} /> Servicios</FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#galeria"><Icon name="Image" size={16} /> Galería</FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#nosotros"><Icon name="UsersRound" size={16} /> Nosotros</FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#contacto"><Icon name="Phone" size={16} /> Contacto</FooterLink>
                </FooterListItem>
                <FooterListItem>
                  <FooterLink href="#contacto"><Icon name="CircleDollarSign" size={16} /> Presupuesto Gratis</FooterLink>
                </FooterListItem>
              </FooterList>
            </FooterColumn>

            <FooterColumn>
              <ColumnTitle>Contacto</ColumnTitle>
              <ContactItem>
                <ContactIcon><Icon name="Smartphone" /></ContactIcon>
                <div>
                  <strong>WhatsApp / Teléfono:</strong><br />
                  +52 951 321 4264
                </div>
              </ContactItem>
              <ContactItem>
                <ContactIcon><Icon name="Mail" /></ContactIcon>
                <div>
                  <strong>Email:</strong><br />
                  contacto@vidrerialacanadita.com
                </div>
              </ContactItem>
              <ContactItem>
                <ContactIcon><Icon name="MapPin" /></ContactIcon>
                <div>
                  <strong>Ubicación:</strong><br />
                  Col. La cañadita Santa María Atzompa
                </div>
              </ContactItem>
              <ContactItem>
                <ContactIcon><Icon name="Clock3" /></ContactIcon>
                <div>
                  <strong>Horarios:</strong><br />
                  Lun - Vie: 8:00 AM - 6:00 PM<br />
                  Sáb: 8:00 AM - 2:00 PM
                </div>
              </ContactItem>
            </FooterColumn>
          </FooterGrid>
        </Container>
      </MainFooter>

      <FooterBottom>
        <Container>
          <BottomContent>
            <Copyright>
              &copy; {currentYear} Vidrería la Cañadita. Vidrios y Aluminios. Todos los derechos reservados.
            </Copyright>
            
            <LegalLinks>
              <LegalLink href="#" onClick={(e) => e.preventDefault()}>
                Política de Privacidad
              </LegalLink>
              <LegalLink href="#" onClick={(e) => e.preventDefault()}>
                Términos y Condiciones
              </LegalLink>
            </LegalLinks>

            <PoweredBy>
              Desarrollado por{' '}
              <a href="https://kinetia.com.mx" target="_blank" rel="noopener noreferrer">
                Kinetia
              </a>
            </PoweredBy>
          </BottomContent>
        </Container>
      </FooterBottom>
    </FooterSection>
  );
};
